import { assert } from '@awayjs/graphics';
import { Scope } from './run/Scope';
import { interpreterWriter, executionWriter, sliceArguments } from './run/writers';
import { HasNext2Info } from './run/HasNext2Info';
import { scopeStacks } from './run/scopeStacks';
import { axEquals } from './run/axEquals';
import { validateCall } from './run/validateCall';
import { validateConstruct } from './run/validateConstruct';
import { axGetDescendants } from './run/axGetDescendants';
import { axCoerceString } from './run/axCoerceString';
import { axCheckFilter } from './run/axCheckFilter';
import { axConvertString } from './run/axConvertString';
import { axAdd } from './run/axAdd';
import { axTypeOf } from './run/axTypeOf';
import { release, notImplemented, popManyInto, getPropertyDescriptor, isNumeric, AVMStage } from '@awayfl/swf-loader';
import { Multiname } from './abc/lazy/Multiname';
import { internNamespace } from './abc/lazy/internNamespace';
import { getBytecodeName } from './abc/ops';
import { escapeAttributeValue, escapeElementValue } from './natives/xml';
import { Errors } from './errors';
import { isValidASValue } from './run/initializeAXBasePrototype';
import { axCoerceName } from './run/axCoerceName';
import { compile, Context } from './jit';
import { Settings } from './Settings';
import { reconstructMetadata, nextScriptID } from './utils/reconstructMetadata';
var MAX_REPORTED_FAILINGS = 100;
var FAILINGS = 0;
/**
 * Helps the interpreter allocate fewer Scope objects.
 */
var ScopeStack = /** @class */ (function () {
    function ScopeStack(parent) {
        this.parent = parent;
        this.stack = [];
        this.isWith = [];
    }
    ScopeStack.prototype.push = function (object, isWith) {
        this.stack.push(object);
        this.isWith.push(!!isWith);
    };
    ScopeStack.prototype.get = function (index) {
        return this.stack[index];
    };
    ScopeStack.prototype.clear = function () {
        this.stack.length = 0;
        this.isWith.length = 0;
    };
    ScopeStack.prototype.pop = function () {
        this.isWith.pop();
        this.stack.pop();
        if (this.scopes && this.scopes.length > this.stack.length) {
            this.scopes.length--;
            release || assert(this.scopes.length === this.stack.length);
        }
    };
    ScopeStack.prototype.topScope = function () {
        if (!this.scopes) {
            if (this.stack.length === 0) {
                return this.parent;
            }
            this.scopes = [];
        }
        var parent = this.parent;
        for (var i = 0; i < this.stack.length; i++) {
            var object = this.stack[i], isWith = this.isWith[i], scope = this.scopes[i];
            if (!scope || scope.parent !== parent || scope.object !== object || scope.isWith !== isWith) {
                scope = this.scopes[i] = new Scope(parent, object, isWith);
            }
            parent = scope;
        }
        return parent;
    };
    return ScopeStack;
}());
export { ScopeStack };
function popNameInto(stack, mn, rn) {
    rn.resolved = {};
    rn.script = null;
    rn.numeric = false;
    rn.id = mn.id;
    rn.kind = mn.kind;
    if (mn.isRuntimeName()) {
        var name_1 = stack.pop();
        // Unwrap content script-created AXQName instances.
        if (name_1 && name_1.axClass && name_1.axClass === name_1.sec.AXQName) {
            name_1 = name_1.name;
            release || assert(name_1 instanceof Multiname);
            rn.kind = mn.isAttribute() ? 18 /* RTQNameLA */ : 17 /* RTQNameL */;
            rn.id = name_1.id;
            rn.name = name_1.name;
            rn.namespaces = name_1.namespaces;
            return;
        }
        if (typeof name_1 === 'number' || isNumeric(axCoerceName(name_1))) {
            rn.numeric = true;
            rn.numericValue = +(axCoerceName(name_1));
        }
        rn.name = name_1;
        rn.id = -1;
    }
    else {
        rn.name = mn.name;
    }
    if (mn.isRuntimeNamespace()) {
        var ns = stack.pop();
        // Unwrap content script-created AXNamespace instances.
        if (ns._ns) {
            release || assert(ns.sec && ns.axClass === ns.sec.AXNamespace);
            ns = ns._ns;
        }
        rn.namespaces = [ns];
        rn.id = -1;
    }
    else {
        rn.namespaces = mn.namespaces;
    }
    interpreterWriter && interpreterWriter.greenLn('Name: ' + rn.name);
}
export function interpret(methodInfo, savedScope, callee) {
    try {
        methodInfo.meta = methodInfo.meta || reconstructMetadata(methodInfo, nextScriptID());
    }
    catch (e) {
        console.warn('[Interpret] Fail reconstruct method metadata:', e);
    }
    if (AVMStage.forceINT && !Settings.NO_FALL_TO_INT) {
        try {
            var result = _interpret(methodInfo, savedScope, callee);
            executionWriter && executionWriter.leave('< ' + methodInfo.trait);
            return result;
        }
        catch (e) {
            executionWriter && executionWriter.leave('< ' + methodInfo.trait + ', Exception: ' + e);
            throw e;
        }
    }
    // empty function for empty body
    if (methodInfo.getBody() == null) {
        return function () { };
    }
    // Try to compile
    if (methodInfo.state === "pending" /* PENDING */) {
        var r = compile(methodInfo, { scope: savedScope });
        if (r.error) {
            methodInfo.error = r.error;
        }
        else {
            methodInfo.compiled = r.compiled;
            methodInfo.names = r.names;
        }
    }
    if (methodInfo.state === "compiled" /* COMPILLED */) {
        methodInfo.useCount++;
        return methodInfo.compiled(new Context(methodInfo, savedScope, methodInfo.names));
    }
    if (Settings.NO_FALL_TO_INT) {
        console.warn('[Interpret] NO_FALL_TO_INT is used, interpret is disable. Fix JIT machin or disable NO_FALL_TO_INT');
        throw methodInfo.error.message + '\n Method:' + methodInfo.meta.classPath;
    }
    methodInfo.useCount++;
    return _interpret(methodInfo, savedScope, callee);
}
var InterpreterFrame = /** @class */ (function () {
    function InterpreterFrame(receiver, methodInfo, parentScope, callArgs, callee) {
        this.pc = 0;
        this.stack = [];
        this.hasNext2Infos = null;
        var body = this.body = methodInfo.getBody();
        this.code = body.code;
        this.scopes = new ScopeStack(parentScope);
        var locals = this.locals = [receiver];
        var app = this.app = methodInfo.abc.applicationDomain;
        var sec = this.sec = app.sec;
        var argCount = callArgs.length;
        var arg;
        for (var i = 0, j = methodInfo.parameters.length; i < j; i++) {
            var p = methodInfo.parameters[i];
            if (i < argCount) {
                arg = callArgs[i];
            }
            else if (p.hasOptionalValue()) {
                arg = p.getOptionalValue();
            }
            else {
                arg = undefined;
            }
            var rn = p.getType();
            if (rn && !rn.isAnyName()) {
                var type = parentScope.getScopeProperty(rn, true, false);
                if (!type) {
                    // During class initialization the class' constructor isn't in scope and can't be
                    // resolved as a scope property: trying to do so yields `null`.
                    // However, using it as a constructor parameter type *does* work, and correctly
                    // applies coercion to the constructor. It's unclear why and how this works in
                    // Tamarin, but since it does work, we check for this scenario here and work around it.
                    if ('axClass' in receiver && receiver.axClass.name.matches(rn)) {
                        type = receiver.axClass;
                    }
                    else {
                        continue;
                    }
                }
                if (!release && interpreterWriter) {
                    interpreterWriter.writeLn('Coercing argument to type ' +
                        (type.axClass ? type.axClass.name.toFQNString(false) : type));
                }
                arg = type.axCoerce(arg);
            }
            locals.push(arg);
        }
        if (methodInfo.needsRest()) {
            locals.push(sec.createArrayUnsafe(sliceArguments(callArgs, methodInfo.parameters.length)));
        }
        else if (methodInfo.needsArguments()) {
            var argsArray = sliceArguments(callArgs, 0);
            var argumentsArray = Object.create(sec.argumentsPrototype);
            argumentsArray.value = argsArray;
            argumentsArray.callee = callee;
            argumentsArray.receiver = receiver;
            argumentsArray.methodInfo = methodInfo;
            locals.push(argumentsArray);
        }
    }
    InterpreterFrame.prototype.bc = function () {
        return this.code[this.pc++];
    };
    InterpreterFrame.prototype.u30 = function () {
        var code = this.code;
        var pc = this.pc;
        var result = code[pc++];
        if (result & 0x80) {
            result = result & 0x7f | code[pc++] << 7;
            if (result & 0x4000) {
                result = result & 0x3fff | code[pc++] << 14;
                if (result & 0x200000) {
                    result = result & 0x1fffff | code[pc++] << 21;
                    if (result & 0x10000000) {
                        result = result & 0x0fffffff | code[pc++] << 28;
                        result = result & 0xffffffff;
                    }
                }
            }
        }
        this.pc = pc;
        return result >>> 0;
    };
    InterpreterFrame.prototype.s24 = function () {
        var code = this.code;
        var pc = this.pc;
        var u = code[pc] | (code[pc + 1] << 8) | (code[pc + 2] << 16);
        this.pc = pc + 3;
        return (u << 8) >> 8;
    };
    InterpreterFrame.prototype.getHasNext2Info = function () {
        var pc = this.pc;
        var hasNext2Infos = this.hasNext2Infos;
        if (!hasNext2Infos) {
            hasNext2Infos = this.hasNext2Infos = [];
        }
        if (!hasNext2Infos[pc]) {
            hasNext2Infos[pc] = new HasNext2Info(null, 0);
        }
        return hasNext2Infos[pc];
    };
    return InterpreterFrame;
}());
function _interpret(methodInfo, savedScope, callee) {
    if (methodInfo.error && methodInfo.useCount <= 1) {
        FAILINGS++;
        if (FAILINGS < MAX_REPORTED_FAILINGS) {
            console.error('[INTERPRET] Compilation failed', methodInfo.error.reason, methodInfo.error.message);
        }
        else {
            console.debug('[INTERPRET] To many compiler failings:', FAILINGS);
        }
    }
    return function () {
        var frame = new InterpreterFrame(this, methodInfo, savedScope, arguments, callee);
        var stack = frame.stack;
        var locals = frame.locals;
        var scopes = frame.scopes;
        var sec = frame.sec;
        var abc = methodInfo.abc;
        var rn = new Multiname(abc, 0, null, null, null, null, true);
        var value, object, receiver, a, b, offset, index, result;
        var args = [];
        var argCount = 0;
        var scopeStacksHeight = scopeStacks.length;
        scopeStacks.push(frame.scopes);
        interpretLabel: while (true) {
            if (!release && interpreterWriter) {
                interpreterWriter.greenLn('' + frame.pc + ': ' + getBytecodeName(frame.code[frame.pc]) + ' [' +
                    frame.stack.map(function (x) { return stringifyStackEntry(x); }).join(', ') + ']');
            }
            try {
                var bc = frame.bc();
                switch (bc) {
                    case 9 /* LABEL */:
                        continue;
                    case 3 /* THROW */:
                        throw stack.pop();
                    case 8 /* KILL */:
                        locals[frame.u30()] = undefined;
                        break;
                    case 12 /* IFNLT */:
                    case 24 /* IFGE */:
                        offset = frame.s24();
                        if (stack.pop() <= stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 13 /* IFNLE */:
                    case 23 /* IFGT */:
                        offset = frame.s24();
                        if (stack.pop() < stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 14 /* IFNGT */:
                    case 22 /* IFLE */:
                        offset = frame.s24();
                        if (stack.pop() >= stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 15 /* IFNGE */:
                    case 21 /* IFLT */:
                        offset = frame.s24();
                        if (stack.pop() > stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 16 /* JUMP */:
                        frame.pc = frame.s24() + frame.pc;
                        continue;
                    case 17 /* IFTRUE */:
                        offset = frame.s24();
                        if (stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 18 /* IFFALSE */:
                        offset = frame.s24();
                        if (!stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 19 /* IFEQ */:
                        offset = frame.s24();
                        if (axEquals(stack.pop(), stack.pop(), sec)) {
                            frame.pc += offset;
                        }
                        continue;
                    case 20 /* IFNE */:
                        offset = frame.s24();
                        if (!axEquals(stack.pop(), stack.pop(), sec)) {
                            frame.pc += offset;
                        }
                        continue;
                    case 25 /* IFSTRICTEQ */:
                        offset = frame.s24();
                        if (stack.pop() === stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 26 /* IFSTRICTNE */:
                        offset = frame.s24();
                        if (stack.pop() !== stack.pop()) {
                            frame.pc += offset;
                        }
                        continue;
                    case 27 /* LOOKUPSWITCH */:
                        var basePC = frame.pc - 1;
                        offset = frame.s24();
                        var caseCount = frame.u30();
                        index = stack.pop();
                        if (index <= caseCount) {
                            frame.pc += 3 * index; // Jump to case offset.
                            offset = frame.s24();
                        }
                        frame.pc = basePC + offset;
                        continue;
                    case 29 /* POPSCOPE */:
                        scopes.pop();
                        break;
                    case 30 /* NEXTNAME */:
                        stack[stack.length - 2] = sec.box(stack[stack.length - 2]).axNextName(stack.pop());
                        break;
                    case 35 /* NEXTVALUE */:
                        stack[stack.length - 2] = sec.box(stack[stack.length - 2]).axNextValue(stack.pop());
                        break;
                    case 50 /* HASNEXT2 */:
                        var hasNext2Info = frame.getHasNext2Info();
                        var objectIndex = frame.u30();
                        var indexIndex = frame.u30();
                        hasNext2Info.next(sec.box(locals[objectIndex]), locals[indexIndex]);
                        locals[objectIndex] = hasNext2Info.object;
                        locals[indexIndex] = hasNext2Info.index;
                        stack.push(!!hasNext2Info.index);
                        break;
                    case 32 /* PUSHNULL */:
                        stack.push(null);
                        break;
                    case 33 /* PUSHUNDEFINED */:
                        stack.push(undefined);
                        break;
                    case 36 /* PUSHBYTE */:
                        stack.push(frame.code[frame.pc++] << 24 >> 24);
                        break;
                    case 37 /* PUSHSHORT */:
                        stack.push(frame.u30() << 16 >> 16);
                        break;
                    case 44 /* PUSHSTRING */:
                        stack.push(abc.getString(frame.u30()));
                        break;
                    case 45 /* PUSHINT */:
                        stack.push(abc.ints[frame.u30()]);
                        break;
                    case 46 /* PUSHUINT */:
                        stack.push(abc.uints[frame.u30()]);
                        break;
                    case 47 /* PUSHDOUBLE */:
                        stack.push(abc.doubles[frame.u30()]);
                        break;
                    case 38 /* PUSHTRUE */:
                        stack.push(true);
                        break;
                    case 39 /* PUSHFALSE */:
                        stack.push(false);
                        break;
                    case 40 /* PUSHNAN */:
                        stack.push(NaN);
                        break;
                    case 41 /* POP */:
                        stack.pop();
                        break;
                    case 42 /* DUP */:
                        stack.push(stack[stack.length - 1]);
                        break;
                    case 43 /* SWAP */:
                        value = stack[stack.length - 1];
                        stack[stack.length - 1] = stack[stack.length - 2];
                        stack[stack.length - 2] = value;
                        break;
                    case 48 /* PUSHSCOPE */:
                        scopes.push(sec.box(stack.pop()), false);
                        break;
                    case 28 /* PUSHWITH */:
                        scopes.push(sec.box(stack.pop()), true);
                        break;
                    case 49 /* PUSHNAMESPACE */:
                        stack.push(sec.AXNamespace.FromNamespace(abc.getNamespace(frame.u30())));
                        break;
                    case 64 /* NEWFUNCTION */:
                        stack.push(sec.createFunction(abc.getMethodInfo(frame.u30()), scopes.topScope(), true));
                        break;
                    case 65 /* CALL */:
                        popManyInto(stack, frame.u30(), args);
                        object = stack.pop();
                        value = stack[stack.length - 1];
                        validateCall(sec, value, args.length);
                        stack[stack.length - 1] = value.axApply(object, args);
                        break;
                    case 66 /* CONSTRUCT */:
                        popManyInto(stack, frame.u30(), args);
                        receiver = sec.box(stack[stack.length - 1]);
                        validateConstruct(sec, receiver, args.length);
                        stack[stack.length - 1] = receiver.axConstruct(args);
                        break;
                    case 71 /* RETURNVOID */:
                        release || assert(scopeStacks.length === scopeStacksHeight + 1);
                        scopeStacks.length--;
                        return;
                    case 72 /* RETURNVALUE */:
                        value = stack.pop();
                        // TODO: ensure proper unwinding of the scope stack.
                        if (methodInfo.returnTypeNameIndex) {
                            receiver = methodInfo.getType();
                            if (receiver) {
                                value = receiver.axCoerce(value);
                            }
                        }
                        release || assert(scopeStacks.length === scopeStacksHeight + 1);
                        scopeStacks.length--;
                        return value;
                    case 73 /* CONSTRUCTSUPER */:
                        popManyInto(stack, frame.u30(), args);
                        savedScope.object.superClass.tPrototype.axInitializer.apply(stack.pop(), args);
                        break;
                    case 74 /* CONSTRUCTPROP */:
                        index = frame.u30();
                        popManyInto(stack, frame.u30(), args);
                        popNameInto(stack, abc.getMultiname(index), rn);
                        stack[stack.length - 1] = sec.box(stack[stack.length - 1]).axConstructProperty(rn, args);
                        break;
                    case 76 /* CALLPROPLEX */:
                    case 70 /* CALLPROPERTY */:
                    case 79 /* CALLPROPVOID */:
                        index = frame.u30();
                        popManyInto(stack, frame.u30(), args);
                        popNameInto(stack, abc.getMultiname(index), rn);
                        result = sec.box(stack[stack.length - 1]).axCallProperty(rn, args, bc === 76 /* CALLPROPLEX */);
                        if (bc === 79 /* CALLPROPVOID */) {
                            stack.length--;
                        }
                        else {
                            stack[stack.length - 1] = result;
                        }
                        break;
                    case 69 /* CALLSUPER */:
                    case 78 /* CALLSUPERVOID */:
                        index = frame.u30();
                        popManyInto(stack, frame.u30(), args);
                        popNameInto(stack, abc.getMultiname(index), rn);
                        result = sec.box(stack[stack.length - 1]).axCallSuper(rn, savedScope, args);
                        if (bc === 78 /* CALLSUPERVOID */) {
                            stack.length--;
                        }
                        else {
                            stack[stack.length - 1] = result;
                        }
                        break;
                    //case Bytecode.CALLSTATIC:
                    //  index = frame.u30();
                    //  argCount = frame.u30();
                    //  popManyInto(stack, argCount, args);
                    //  value = abc.getMetadataInfo(index);
                    //  var receiver = box(stack[stack.length - 1]);
                    //  stack.push(value.axApply(receiver, args));
                    //  break;
                    case 83 /* APPLYTYPE */:
                        popManyInto(stack, frame.u30(), args);
                        stack[stack.length - 1] = sec.applyType(stack[stack.length - 1], args);
                        break;
                    case 85 /* NEWOBJECT */:
                        object = Object.create(sec.AXObject.tPrototype);
                        argCount = frame.u30();
                        // For LIFO-order iteration to be correct, we have to add items highest on the stack
                        // first.
                        for (var i = stack.length - argCount * 2; i < stack.length; i += 2) {
                            value = stack[i + 1];
                            object.axSetPublicProperty(stack[i], value);
                        }
                        stack.length -= argCount * 2;
                        stack.push(object);
                        break;
                    case 86 /* NEWARRAY */:
                        object = [];
                        argCount = frame.u30();
                        for (var i = stack.length - argCount; i < stack.length; i++) {
                            object.push(stack[i]);
                        }
                        stack.length -= argCount;
                        stack.push(sec.AXArray.axBox(object));
                        break;
                    case 87 /* NEWACTIVATION */:
                        stack.push(sec.createActivation(methodInfo, scopes.topScope()));
                        break;
                    case 88 /* NEWCLASS */:
                        // Storing super class in `value` to make exception handling easier.
                        value = stack[stack.length - 1];
                        stack[stack.length - 1] = sec.createClass(abc.classes[frame.u30()], value, scopes.topScope());
                        break;
                    case 89 /* GETDESCENDANTS */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        if (rn.name === undefined)
                            rn.name = '*';
                        stack[stack.length - 1] = axGetDescendants(stack[stack.length - 1], rn, sec);
                        break;
                    case 90 /* NEWCATCH */:
                        stack.push(sec.createCatch(frame.body.catchBlocks[frame.u30()], scopes.topScope()));
                        break;
                    case 94 /* FINDPROPERTY */:
                    case 93 /* FINDPROPSTRICT */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack.push(scopes.topScope().findScopeProperty(rn, bc === 93 /* FINDPROPSTRICT */, false));
                        break;
                    case 96 /* GETLEX */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack.push(scopes.topScope().findScopeProperty(rn, true, false).axGetProperty(rn));
                        break;
                    case 104 /* INITPROPERTY */:
                    case 97 /* SETPROPERTY */:
                        value = stack.pop();
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        sec.box(stack.pop()).axSetProperty(rn, value, 104 /* INITPROPERTY */, methodInfo);
                        break;
                    case 102 /* GETPROPERTY */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack[stack.length - 1] = sec.box(stack[stack.length - 1]).axGetProperty(rn);
                        break;
                    case 106 /* DELETEPROPERTY */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack[stack.length - 1] = sec.box(stack[stack.length - 1]).axDeleteProperty(rn);
                        break;
                    case 4 /* GETSUPER */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack[stack.length - 1] = sec.box(stack[stack.length - 1]).axGetSuper(rn, savedScope);
                        break;
                    case 5 /* SETSUPER */:
                        value = stack.pop();
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        sec.box(stack.pop()).axSetSuper(rn, savedScope, value);
                        break;
                    case 98 /* GETLOCAL */:
                        stack.push(locals[frame.u30()]);
                        break;
                    case 99 /* SETLOCAL */:
                        locals[frame.u30()] = stack.pop();
                        break;
                    case 100 /* GETGLOBALSCOPE */:
                        stack.push(savedScope.global.object);
                        break;
                    case 101 /* GETSCOPEOBJECT */:
                        stack.push(scopes.get(frame.code[frame.pc++]));
                        break;
                    case 108 /* GETSLOT */:
                        stack[stack.length - 1] = sec.box(stack[stack.length - 1]).axGetSlot(frame.u30());
                        break;
                    case 109 /* SETSLOT */:
                        value = stack.pop();
                        sec.box(stack.pop()).axSetSlot(frame.u30(), value);
                        break;
                    case 110 /* GETGLOBALSLOT */:
                        stack[stack.length - 1] = savedScope.global.object.axGetSlot(frame.u30());
                        break;
                    case 111 /* SETGLOBALSLOT */:
                        value = stack.pop();
                        savedScope.global.object.axSetSlot(frame.u30(), value);
                        break;
                    case 114 /* ESC_XATTR */:
                        stack[stack.length - 1] = escapeAttributeValue(stack[stack.length - 1]);
                        break;
                    case 113 /* ESC_XELEM */:
                        stack[stack.length - 1] = escapeElementValue(sec, stack[stack.length - 1]);
                        break;
                    case 131 /* COERCE_I */:
                    case 115 /* CONVERT_I */:
                        stack[stack.length - 1] |= 0;
                        break;
                    case 136 /* COERCE_U */:
                    case 116 /* CONVERT_U */:
                        stack[stack.length - 1] >>>= 0;
                        break;
                    case 132 /* COERCE_D */:
                    case 117 /* CONVERT_D */:
                        stack[stack.length - 1] = +stack[stack.length - 1];
                        break;
                    case 129 /* COERCE_B */:
                    case 118 /* CONVERT_B */:
                        stack[stack.length - 1] = !!stack[stack.length - 1];
                        break;
                    case 133 /* COERCE_S */:
                        stack[stack.length - 1] = axCoerceString(stack[stack.length - 1]);
                        break;
                    case 112 /* CONVERT_S */:
                        stack[stack.length - 1] = axConvertString(stack[stack.length - 1]);
                        break;
                    case 120 /* CHECKFILTER */:
                        stack[stack.length - 1] = axCheckFilter(sec, stack[stack.length - 1]);
                        break;
                    case 128 /* COERCE */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack[stack.length - 1] = scopes.topScope().getScopeProperty(rn, true, false).axCoerce(stack[stack.length - 1]);
                        break;
                    case 130 /* COERCE_A */: /* NOP */
                        break;
                    case 134 /* ASTYPE */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack[stack.length - 1] = scopes.topScope().getScopeProperty(rn, true, false).axAsType(stack[stack.length - 1]);
                        break;
                    case 135 /* ASTYPELATE */:
                        stack[stack.length - 2] = stack.pop().axAsType(stack[stack.length - 1]);
                        break;
                    case 137 /* COERCE_O */:
                        object = stack[stack.length - 1];
                        stack[stack.length - 1] = object == undefined ? null : object;
                        break;
                    case 144 /* NEGATE */:
                        stack[stack.length - 1] = -stack[stack.length - 1];
                        break;
                    case 145 /* INCREMENT */:
                        ++stack[stack.length - 1];
                        break;
                    case 146 /* INCLOCAL */:
                        ++locals[frame.u30()];
                        break;
                    case 147 /* DECREMENT */:
                        --stack[stack.length - 1];
                        break;
                    case 148 /* DECLOCAL */:
                        --locals[frame.u30()];
                        break;
                    case 149 /* TYPEOF */:
                        stack[stack.length - 1] = axTypeOf(stack[stack.length - 1], sec);
                        break;
                    case 150 /* NOT */:
                        stack[stack.length - 1] = !stack[stack.length - 1];
                        break;
                    case 151 /* BITNOT */:
                        stack[stack.length - 1] = ~stack[stack.length - 1];
                        break;
                    case 160 /* ADD */:
                        b = stack.pop();
                        a = stack[stack.length - 1];
                        if (typeof a === 'number' && typeof b === 'number') {
                            stack[stack.length - 1] = a + b;
                        }
                        else {
                            stack[stack.length - 1] = axAdd(a, b, sec);
                        }
                        break;
                    case 161 /* SUBTRACT */:
                        stack[stack.length - 2] -= stack.pop();
                        break;
                    case 162 /* MULTIPLY */:
                        stack[stack.length - 2] *= stack.pop();
                        break;
                    case 163 /* DIVIDE */:
                        stack[stack.length - 2] /= stack.pop();
                        break;
                    case 164 /* MODULO */:
                        stack[stack.length - 2] %= stack.pop();
                        break;
                    case 165 /* LSHIFT */:
                        stack[stack.length - 2] <<= stack.pop();
                        break;
                    case 166 /* RSHIFT */:
                        stack[stack.length - 2] >>= stack.pop();
                        break;
                    case 167 /* URSHIFT */:
                        stack[stack.length - 2] >>>= stack.pop();
                        break;
                    case 168 /* BITAND */:
                        stack[stack.length - 2] &= stack.pop();
                        break;
                    case 169 /* BITOR */:
                        stack[stack.length - 2] |= stack.pop();
                        break;
                    case 170 /* BITXOR */:
                        stack[stack.length - 2] ^= stack.pop();
                        break;
                    case 171 /* EQUALS */:
                        stack[stack.length - 2] = axEquals(stack[stack.length - 2], stack.pop(), sec);
                        break;
                    case 172 /* STRICTEQUALS */:
                        stack[stack.length - 2] = stack[stack.length - 2] === stack.pop();
                        break;
                    case 173 /* LESSTHAN */:
                        stack[stack.length - 2] = stack[stack.length - 2] < stack.pop();
                        break;
                    case 174 /* LESSEQUALS */:
                        stack[stack.length - 2] = stack[stack.length - 2] <= stack.pop();
                        break;
                    case 175 /* GREATERTHAN */:
                        stack[stack.length - 2] = stack[stack.length - 2] > stack.pop();
                        break;
                    case 176 /* GREATEREQUALS */:
                        stack[stack.length - 2] = stack[stack.length - 2] >= stack.pop();
                        break;
                    case 177 /* INSTANCEOF */:
                        stack[stack.length - 2] = stack.pop().axIsInstanceOf(stack[stack.length - 1]);
                        break;
                    case 178 /* ISTYPE */:
                        popNameInto(stack, abc.getMultiname(frame.u30()), rn);
                        stack[stack.length - 1] = scopes.topScope().findScopeProperty(rn, true, false).axIsType(stack[stack.length - 1]);
                        break;
                    case 179 /* ISTYPELATE */:
                        stack[stack.length - 2] = stack.pop().axIsType(stack[stack.length - 1]);
                        break;
                    case 180 /* IN */:
                        receiver = sec.box(stack.pop());
                        var name = stack[stack.length - 1];
                        stack[stack.length - 1] = (name && name.axClass === sec.AXQName) ? receiver.axHasProperty(name.name) : receiver.axHasPublicProperty(name);
                        break;
                    case 192 /* INCREMENT_I */:
                        stack[stack.length - 1] = (stack[stack.length - 1] | 0) + 1;
                        break;
                    case 193 /* DECREMENT_I */:
                        stack[stack.length - 1] = (stack[stack.length - 1] | 0) - 1;
                        break;
                    case 194 /* INCLOCAL_I */:
                        index = frame.u30();
                        locals[index] = (locals[index] | 0) + 1;
                        break;
                    case 195 /* DECLOCAL_I */:
                        index = frame.u30();
                        locals[index] = (locals[index] | 0) - 1;
                        break;
                    case 196 /* NEGATE_I */:
                        stack[stack.length - 1] = -(stack[stack.length - 1] | 0);
                        break;
                    case 197 /* ADD_I */:
                        stack[stack.length - 2] = (stack[stack.length - 2] | 0) + (stack.pop() | 0) | 0;
                        break;
                    case 198 /* SUBTRACT_I */:
                        stack[stack.length - 2] = (stack[stack.length - 2] | 0) - (stack.pop() | 0) | 0;
                        break;
                    case 199 /* MULTIPLY_I */:
                        stack[stack.length - 2] = (stack[stack.length - 2] | 0) * (stack.pop() | 0) | 0;
                        break;
                    case 208 /* GETLOCAL0 */:
                        stack.push(locals[0]);
                        break;
                    case 209 /* GETLOCAL1 */:
                        stack.push(locals[1]);
                        break;
                    case 210 /* GETLOCAL2 */:
                        stack.push(locals[2]);
                        break;
                    case 211 /* GETLOCAL3 */:
                        stack.push(locals[3]);
                        break;
                    case 212 /* SETLOCAL0 */:
                        locals[0] = stack.pop();
                        break;
                    case 213 /* SETLOCAL1 */:
                        locals[1] = stack.pop();
                        break;
                    case 214 /* SETLOCAL2 */:
                        locals[2] = stack.pop();
                        break;
                    case 215 /* SETLOCAL3 */:
                        locals[3] = stack.pop();
                        break;
                    case 6 /* DXNS */:
                        scopes.topScope().defaultNamespace = internNamespace(0 /* Public */, abc.getString(frame.u30()));
                        break;
                    case 7 /* DXNSLATE */:
                        scopes.topScope().defaultNamespace = internNamespace(0 /* Public */, stack.pop());
                        break;
                    case 239 /* DEBUG */:
                        frame.pc++;
                        frame.u30();
                        frame.pc++;
                        frame.u30();
                        break;
                    case 240 /* DEBUGLINE */:
                    case 241 /* DEBUGFILE */:
                        frame.u30();
                        break;
                    case 2 /* NOP */:
                    case 1 /* BKPT */:
                        break;
                    default:
                        notImplemented(getBytecodeName(bc));
                }
            }
            catch (e) {
                // TODO: e = translateError(e);
                // All script exceptions must be primitive or have a security domain, if they don't then
                // this must be a VM exception.
                if (!isValidASValue(e)) {
                    // We omit many checks in the interpreter loop above to keep the code small. These
                    // checks can be done after the fact here by turning the VM-internal exception into a
                    // proper error according to the current operation.
                    e = createValidException(sec, e, bc, value, receiver, a, b, rn, scopeStacksHeight + 1);
                }
                var catchBlocks = frame.body.catchBlocks;
                for (var i = 0; i < catchBlocks.length; i++) {
                    var handler = catchBlocks[i];
                    if (frame.pc >= handler.start && frame.pc <= handler.end) {
                        var typeName = handler.getType();
                        if (!typeName || frame.app.getClass(typeName).axIsType(e)) {
                            stack.length = 0;
                            stack.push(e);
                            scopes.clear();
                            frame.pc = handler.target;
                            continue interpretLabel;
                        }
                    }
                }
                release || assert(scopeStacks.length === scopeStacksHeight + 1);
                scopeStacks.length--;
                throw e;
            }
        }
    };
}
function createValidException(sec, internalError, bc, value, receiver, a, b, mn, expectedScopeStacksHeight) {
    var isProperErrorObject = internalError instanceof Error &&
        typeof internalError.name === 'string' &&
        typeof internalError.message === 'string';
    if (isProperErrorObject) {
        if (internalError instanceof RangeError || internalError.name === 'InternalError') {
            var obj = Object.create(sec.AXError.tPrototype);
            obj._errorID = 1023;
            // Stack exhaustion errors are annoying to catch: Identifying them requires
            // pattern-matching of error messages, and throwing them must be done very
            // carefully to not cause the next one.
            if (internalError.message === 'allocation size overflow') {
                obj.$Bgmessage = 'allocation size overflow';
                return obj;
            }
            if (internalError.message.indexOf('recursion') > -1 ||
                internalError.message.indexOf('call stack size exceeded') > -1) {
                obj.$Bgmessage = 'Stack overflow occurred';
                scopeStacks.length = expectedScopeStacksHeight;
                return obj;
            }
        }
        else if (internalError instanceof TypeError) {
            if (internalError.message.indexOf('convert') > -1 &&
                (internalError.message.indexOf('to primitive') > -1 ||
                    internalError.message.indexOf('to string') > -1)) {
                return sec.createError('TypeError', Errors.ConvertToPrimitiveError, 'value');
            }
            // Internal error thrown by generic Array methods.
            if (internalError.message === 'Conversion to Array failed') {
                return sec.createError('TypeError', Errors.CheckTypeFailedError, 'value', 'Array');
            }
        }
    }
    var message;
    var isSuper = false;
    switch (bc) {
        case 65 /* CALL */:
            if (!value || !value.axApply) {
                return sec.createError('TypeError', Errors.CallOfNonFunctionError, 'value');
            }
            break;
        case 66 /* CONSTRUCT */:
            if (!receiver || !receiver.axConstruct) {
                return sec.createError('TypeError', Errors.ConstructOfNonFunctionError);
            }
            break;
        case 88 /* NEWCLASS */:
            if (!value || !sec.AXClass.axIsType(value)) {
                return sec.createError('VerifyError', Errors.InvalidBaseClassError);
            }
            break;
        case 78 /* CALLSUPERVOID */:
        case 73 /* CONSTRUCTSUPER */:
            isSuper = true;
        // Fallthrough.
        case 70 /* CALLPROPERTY */:
        case 79 /* CALLPROPVOID */:
        case 76 /* CALLPROPLEX */:
        case 74 /* CONSTRUCTPROP */:
        case 69 /* CALLSUPER */:
            if (receiver === null) {
                return sec.createError('TypeError', Errors.ConvertNullToObjectError);
            }
            if (receiver === undefined) {
                return sec.createError('TypeError', Errors.ConvertUndefinedToObjectError);
            }
            if (!(receiver.axResolveMultiname(mn) in receiver)) {
                var axClass = isSuper ? receiver.axClass.superClass : receiver.axClass;
                if (axClass.classInfo.instanceInfo.isSealed()) {
                    return sec.createError('ReferenceError', Errors.ReadSealedError, mn.name, axClass.name.toFQNString(false));
                }
                return sec.createError('TypeError', isSuper ?
                    Errors.ConstructOfNonFunctionError :
                    Errors.CallOfNonFunctionError, mn.name);
            }
            if (isProperErrorObject && internalError.name === 'RangeError' &&
                (internalError.message.indexOf('arguments array passed') > -1 ||
                    internalError.message.indexOf('call stack size') > -1)) {
                return sec.createError('RangeError', Errors.StackOverflowError);
            }
            break;
        case 4 /* GETSUPER */:
            isSuper = true;
        // Fallthrough.
        case 102 /* GETPROPERTY */:
            if (receiver === null) {
                return sec.createError('TypeError', Errors.ConvertNullToObjectError);
            }
            if (receiver === undefined) {
                return sec.createError('TypeError', Errors.ConvertUndefinedToObjectError);
            }
            break;
        case 104 /* INITPROPERTY */:
        case 97 /* SETPROPERTY */:
            if (receiver === null) {
                return sec.createError('TypeError', Errors.ConvertNullToObjectError);
            }
            if (receiver === undefined) {
                return sec.createError('TypeError', Errors.ConvertUndefinedToObjectError);
            }
            var nm = receiver.axResolveMultiname(mn);
            if (nm in receiver && getPropertyDescriptor(receiver, nm).writable === false) {
                return sec.createError('ReferenceError', Errors.ConstWriteError, nm, receiver.axClass.name.name);
            }
            break;
        case 177 /* INSTANCEOF */:
            if (!receiver || !receiver.axIsInstanceOf) {
                return sec.createError('TypeError', Errors.CantUseInstanceofOnNonObjectError);
            }
            break;
        case 134 /* ASTYPE */:
        case 135 /* ASTYPELATE */:
            // ASTYPE(LATE) have almost the same error messages as ISTYPE(LATE), but not *quite*.
            if (receiver && !receiver.axAsType) {
                return sec.createError('TypeError', Errors.ConvertNullToObjectError);
            }
        // Fallthrough.
        case 178 /* ISTYPE */:
        case 179 /* ISTYPELATE */:
            if (receiver === null) {
                return sec.createError('TypeError', Errors.ConvertNullToObjectError);
            }
            if (receiver === undefined) {
                return sec.createError('TypeError', Errors.ConvertUndefinedToObjectError);
            }
            if (!receiver.axIsType) {
                return sec.createError('TypeError', Errors.IsTypeMustBeClassError);
            }
            break;
        case 128 /* COERCE */:
            if (!receiver) {
                return sec.createError('ReferenceError', Errors.ClassNotFoundError, mn.toFQNString(false));
            }
            break;
        case 180 /* IN */:
            if (receiver === null) {
                return sec.createError('TypeError', Errors.ConvertNullToObjectError);
            }
            if (receiver === undefined) {
                return sec.createError('TypeError', Errors.ConvertUndefinedToObjectError);
            }
            break;
        case 19 /* IFEQ */:
        case 20 /* IFNE */:
        case 171 /* EQUALS */:
            if (typeof a !== typeof b) {
                if (typeof a === 'object' && a && typeof b !== 'object' ||
                    typeof b === 'object' && b && typeof a !== 'object') {
                    return sec.createError('TypeError', Errors.ConvertToPrimitiveError, 'Object');
                }
            }
            break;
        default:
            // Pattern-match some otherwise-annoying-to-convert exceptions. This is all best-effort,
            // so we fail if we're not sure about something.
            if (!internalError || typeof internalError.message !== 'string' ||
                typeof internalError.stack !== 'string' ||
                typeof internalError.name !== 'string') {
                break;
            }
            message = internalError.message;
            var stack = internalError.stack.split('\n');
            var lastFunctionEntry = stack[0].indexOf('at ') === 0 ? stack[0].substr(3) : stack[0];
            switch (internalError.name) {
                case 'TypeError':
                    if (lastFunctionEntry.indexOf('AXBasePrototype_valueOf') === 0 ||
                        lastFunctionEntry.indexOf('AXBasePrototype_toString') === 0) {
                        return sec.createError('TypeError', Errors.CallOfNonFunctionError, 'value');
                    }
            }
    }
    // To be sure we don't let VM exceptions flow into the player, box them manually here,
    // even in release builds.
    message = 'Uncaught VM-internal exception during op ' + getBytecodeName(bc) + ': ';
    var stack;
    try {
        message += internalError.toString();
        stack = internalError.stack;
    }
    catch (e) {
        message += '[Failed to stringify exception]';
    }
    // In the extension, we can just kill all the things.
    var player = sec['player'];
    console.error(message, '\n', stack);
    if (player) {
        //player.executeFSCommand('quit', [message]);
        //} else if (typeof jsGlobal.quit === 'function') {
        //  jsGlobal.quit();
    }
    // In other packagings, at least throw a valid value.
    return sec.createError('Error', Errors.InternalErrorIV);
}
function stringifyStackEntry(x) {
    if (!x || !x.toString) {
        return String(x);
    }
    if (x.$BgtoString && x.$BgtoString.isInterpreted) {
        return '<unprintable ' + (x.axClass ? x.axClass.name.toFQNString(false) : 'object') + '>';
    }
    try {
        return x.toString();
    }
    catch (e) {
        return '<unprintable ' + (x.axClass ? x.axClass.name.toFQNString(false) : 'object') + '>';
    }
}
